import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

/**
 * CardPaymentSection — single, reusable card-entry UI for every checkout in
 * the app. Replaces Stripe's bundled CardElement (which puts the postal-code
 * input in an awkward, region-shifting spot on mobile).
 *
 * What it does:
 *   1. Renders Apple Pay / Google Pay (PaymentRequestButton) when the device
 *      supports it. Tapping it triggers a native wallet sheet that returns a
 *      Stripe PaymentMethod we surface via `onWalletPaymentMethod`.
 *   2. Renders a split card form (number / expiry / cvc) so we own the
 *      layout end-to-end.
 *   3. Renders a country selector that drives the postal-code field
 *      (ZIP for US, postal code for Canada, hidden otherwise) with
 *      country-specific validation.
 *
 * The parent owns the actual submit: it calls our exposed `confirmCard()`
 * to get back a SetupIntent payment_method to ship to the backend. Wallet
 * payments bypass that — when the user taps Apple/Google Pay the wallet
 * sheet collects auth and we deliver the resulting PaymentMethod straight
 * via the callback.
 */

const COUNTRIES = [
  { code: 'US', label: 'United States', postalLabel: 'ZIP code', postalPattern: /^\d{5}(?:-\d{4})?$/, postalPlaceholder: '94110' },
  { code: 'CA', label: 'Canada', postalLabel: 'Postal code', postalPattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, postalPlaceholder: 'V5K 0A1' },
  { code: 'GB', label: 'United Kingdom', postalLabel: 'Postcode', postalPattern: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i, postalPlaceholder: 'SW1A 1AA' },
  { code: 'AU', label: 'Australia', postalLabel: 'Postcode', postalPattern: /^\d{4}$/, postalPlaceholder: '2000' },
  { code: 'DE', label: 'Germany', postalLabel: 'Postleitzahl', postalPattern: /^\d{5}$/, postalPlaceholder: '10115' },
  { code: 'FR', label: 'France', postalLabel: 'Code postal', postalPattern: /^\d{5}$/, postalPlaceholder: '75001' },
  { code: 'OTHER', label: 'Other', postalLabel: null, postalPattern: null, postalPlaceholder: null },
];

const elementOptions = {
  style: {
    base: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: '15px',
      fontSmoothing: 'antialiased',
      color: '#000000',
      '::placeholder': { color: '#9ca3af' },
      iconColor: '#000000',
    },
    invalid: { color: '#dc2626', iconColor: '#dc2626' },
  },
};

/**
 * Imperative API exposed via ref:
 *   confirmCard(clientSecret, { email, name }) → { paymentMethod | error }
 *     For backend SetupIntent-style flows: call after backend returns the
 *     SetupIntent's clientSecret, gets the attached payment method back.
 *
 * Props:
 *   amountCents             — total in cents, used to render the PaymentRequest sheet
 *   currency                — ISO 4217 lowercase, e.g. 'usd', 'cad'
 *   countryDefault          — default 'CA' (most of our base)
 *   onWalletPaymentMethod   — (paymentMethod, completeFn) callback when Apple/Google Pay
 *                             returns a payment method. Caller should ship paymentMethod.id
 *                             to backend; once backend confirms/declines, call completeFn('success'|'fail').
 *   onValidityChange        — (isReadyToSubmit: boolean) lets the parent enable/disable its submit button.
 *   disabled                — pass-through to all elements
 *   label                   — heading text, default 'PAY WITH CARD' (null hides it)
 *   appearance              — 'default' (2px black boxes) or 'pill' (48px #F5F5F5 pills, the
 *                             gathering sheets)
 */
const CardPaymentSection = forwardRef(function CardPaymentSection(
  {
    amountCents,
    currency = 'cad',
    countryDefault = 'CA',
    onWalletPaymentMethod,
    onValidityChange,
    disabled = false,
    label = 'PAY WITH CARD',
    appearance = 'default',
  },
  ref,
) {
  const pill = appearance === 'pill';
  const fieldBox = (complete) => fieldBoxStyle(complete, pill);
  const nativeInputStyle = pill ? { ...nativeInputBase, border: 'none', borderRadius: 999, background: '#F5F5F5', padding: '0 18px' } : nativeInputBase;
  const stripe = useStripe();
  const elements = useElements();
  const [country, setCountry] = useState(countryDefault);
  const [postal, setPostal] = useState('');
  const [postalTouched, setPostalTouched] = useState(false);
  const [cardComplete, setCardComplete] = useState({ number: false, expiry: false, cvc: false });
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletAvailable, setWalletAvailable] = useState(false);

  const countryDef = useMemo(() => COUNTRIES.find((c) => c.code === country) || COUNTRIES[COUNTRIES.length - 1], [country]);
  const postalValid = countryDef.postalPattern ? countryDef.postalPattern.test(postal.trim()) : true;
  const allCardFieldsComplete = cardComplete.number && cardComplete.expiry && cardComplete.cvc;
  const readyToSubmit = !!stripe && !!elements && allCardFieldsComplete && postalValid && !disabled;

  // Bubble validity up so the parent can enable its submit button.
  const lastValidityRef = useRef(false);
  useEffect(() => {
    if (lastValidityRef.current !== readyToSubmit) {
      lastValidityRef.current = readyToSubmit;
      onValidityChange?.(readyToSubmit);
    }
  }, [readyToSubmit, onValidityChange]);

  // Spin up the Stripe PaymentRequest (Apple Pay / Google Pay) when amount/currency
  // changes. canMakePayment() resolves to null on devices/browsers that don't
  // support it — in that case we silently hide the button.
  useEffect(() => {
    if (!stripe || !amountCents || amountCents <= 0) {
      setPaymentRequest(null);
      setWalletAvailable(false);
      return;
    }
    const pr = stripe.paymentRequest({
      country: country === 'OTHER' ? 'US' : country,
      currency: (currency || 'cad').toLowerCase(),
      total: { label: 'Portal', amount: amountCents },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: ['link'],
    });
    let mounted = true;
    pr.canMakePayment().then((result) => {
      if (!mounted) return;
      // Only surface the wallet button when Apple Pay or Google Pay is
      // actually available. Stripe's PaymentRequest will otherwise show
      // its own "Link" wallet, which users mistake for a generic system
      // login and find confusing. Card form stays the fallback.
      const hasNativeWallet = !!result && (result.applePay || result.googlePay);
      if (hasNativeWallet) {
        setPaymentRequest(pr);
        setWalletAvailable(true);
      } else {
        setPaymentRequest(null);
        setWalletAvailable(false);
      }
    });
    // Wire wallet → backend by handing the parent the resulting PaymentMethod.
    pr.on('paymentmethod', async (ev) => {
      if (!onWalletPaymentMethod) {
        ev.complete('fail');
        return;
      }
      const complete = (status) => ev.complete(status === 'success' ? 'success' : 'fail');
      try {
        await onWalletPaymentMethod(ev.paymentMethod, complete);
      } catch {
        complete('fail');
      }
    });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, amountCents, currency, country]);

  useImperativeHandle(ref, () => ({
    /**
     * Confirm a SetupIntent's clientSecret using the card form. Returns
     *   { paymentMethod: string }  on success, or
     *   { error: string }          on failure.
     */
    async confirmSetup(clientSecret, billing = {}) {
      if (!stripe || !elements) return { error: 'Stripe is not loaded' };
      if (!postalValid) {
        setPostalTouched(true);
        return { error: `${countryDef.postalLabel || 'Postal code'} is required` };
      }
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) return { error: 'Card form is not ready' };
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: {
            email: billing.email,
            name: billing.name,
            address: {
              country: country === 'OTHER' ? null : country,
              postal_code: countryDef.postalPattern ? postal.trim() : null,
            },
          },
        },
      });
      if (result.error) return { error: result.error.message || 'Payment failed' };
      return { paymentMethod: result.setupIntent.payment_method };
    },
  }), [stripe, elements, country, countryDef, postal, postalValid]);

  return (
    <div>
      {/* Only when the page actually handles the result. Without a handler the
          sheet opens, gets a PaymentMethod, and has nowhere to send it — which
          is how the Pro booking page shipped an Apple Pay button that spun and
          never charged anything. */}
      {walletAvailable && paymentRequest && onWalletPaymentMethod && (
        <div style={{ marginBottom: 18 }}>
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: { paymentRequestButton: { theme: 'dark', height: '48px', type: 'default' } },
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', margin: '14px 0', gap: 10 }}>
            <div style={{ flex: 1, borderTop: '1px solid #d1d5db' }} />
            <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em' }}>OR PAY WITH CARD</span>
            <div style={{ flex: 1, borderTop: '1px solid #d1d5db' }} />
          </div>
        </div>
      )}

      {!walletAvailable && label && (
        <p style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#000', marginBottom: 10 }}>
          {label}
        </p>
      )}

      {/* Card number */}
      <label style={fieldLabel}>Card number</label>
      <div style={fieldBox(cardComplete.number)}>
        <CardNumberElement
          options={{ ...elementOptions, showIcon: true, placeholder: '1234 1234 1234 1234' }}
          onChange={(e) => setCardComplete((s) => ({ ...s, number: e.complete }))}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <div>
          <label style={fieldLabel}>Expiry</label>
          <div style={fieldBox(cardComplete.expiry)}>
            <CardExpiryElement
              options={{ ...elementOptions, placeholder: 'MM / YY' }}
              onChange={(e) => setCardComplete((s) => ({ ...s, expiry: e.complete }))}
            />
          </div>
        </div>
        <div>
          <label style={fieldLabel}>CVC</label>
          <div style={fieldBox(cardComplete.cvc)}>
            <CardCvcElement
              options={{ ...elementOptions, placeholder: 'CVC' }}
              onChange={(e) => setCardComplete((s) => ({ ...s, cvc: e.complete }))}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <div>
          <label style={fieldLabel}>Country</label>
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPostal(''); setPostalTouched(false); }}
            disabled={disabled}
            style={{
              ...nativeInputStyle,
              appearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              paddingRight: 28,
            }}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>
        {countryDef.postalLabel ? (
          <div>
            <label style={fieldLabel}>{countryDef.postalLabel}</label>
            <input
              type="text"
              inputMode={countryDef.code === 'US' || countryDef.code === 'AU' || countryDef.code === 'DE' || countryDef.code === 'FR' ? 'numeric' : 'text'}
              autoComplete="postal-code"
              value={postal}
              onChange={(e) => setPostal(e.target.value)}
              onBlur={() => setPostalTouched(true)}
              placeholder={countryDef.postalPlaceholder || ''}
              disabled={disabled}
              style={{
                ...nativeInputStyle,
                borderColor: postalTouched && !postalValid ? '#dc2626' : '#000',
                textTransform: countryDef.code === 'CA' || countryDef.code === 'GB' ? 'uppercase' : 'none',
              }}
            />
            {postalTouched && !postalValid && (
              <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                Enter a valid {countryDef.postalLabel.toLowerCase()}
              </p>
            )}
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
});

const fieldLabel = {
  display: 'block',
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#000',
  marginBottom: 4,
  fontWeight: 600,
};

const nativeInputBase = {
  width: '100%',
  border: '2px solid #000',
  borderRadius: 0,
  padding: '12px 14px',
  fontSize: 15,
  fontFamily: 'ui-monospace, monospace',
  background: 'white',
  color: '#000',
  outline: 'none',
  boxSizing: 'border-box',
  height: 48,
};

function fieldBoxStyle(complete, pill = false) {
  if (pill) return { border: 'none', borderRadius: 999, padding: '14px 18px', background: '#F5F5F5', minHeight: 48, boxSizing: 'border-box' };
  return {
    border: '2px solid #000',
    borderRadius: 0,
    padding: '14px',
    background: 'white',
    minHeight: 48,
    boxSizing: 'border-box',
    boxShadow: complete ? 'inset 0 0 0 0 transparent' : undefined,
  };
}

export default CardPaymentSection;
